import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';

import { LayoutAuth } from 'layout/LayoutAuth';
import LayoutDashboard from 'layout/LayoutDashboard';
import LayoutMain from 'layout/LayoutMain';

import { AdminUsers } from './AdminUsers/AdminUsers';
import { Dashboard } from './Dashboard/Dashboard';
import { ErrorPage } from './ErrorPage/ErrorPage';
import { Gateways } from './Gateways';
import { Login } from './Login/Login';
import SensorDetails from './SensorDetails';
import { UserCreate } from './UserCreate';
import Welcome from './Welcome/Welcome';
import { ZoneDetails } from './ZoneDetails/ZoneDetails';

import { RoutesType } from 'entities/view';
import { IRootStore } from 'store/Root.reducer';
import { PAGES } from 'utils/link';
import SubscriptionView from './Subscriptions';

const HomeRedirect = () => {
  const user = useSelector((state: IRootStore) => state.auth.user);

  if (!user) {
    return <Navigate to={PAGES.AUTH_LOGIN} replace />;
  }

  if (user.role === 'admin') {
    return <Navigate to={PAGES.ADMIN_USERS} replace />;
  }

  const hasSensors = (user.meta?.sensors_count ?? 0) > 0;
  if (hasSensors) {
    return <Navigate to={PAGES.DASHBOARD.replace(':id', user.id.toString())} replace />;
  }

  return <Navigate to={PAGES.WELCOME} replace />;
};

const clientRoutes: RoutesType[] = [
  {
    path: PAGES.HOME_PAGE,
    element: <HomeRedirect />,
    name: 'ViewHome',
    Layout: LayoutMain,
    isPrivate: true,
  },
  {
    path: PAGES.DASHBOARD,
    element: <Dashboard />,
    name: 'ViewDashboard',
    Layout: LayoutDashboard,
    showBackButton: true,
    isPrivate: true,
    title: (state) => state.admin.dashboard.data?.fullname || 'Dashboard',
  },
  {
    path: PAGES.WELCOME,
    element: <Welcome />,
    name: 'ViewWelcome',
    Layout: LayoutMain,
    showBackButton: false,
    isPrivate: true,
  },
  {
    path: PAGES.AUTH_LOGIN,
    element: <Login />,
    name: 'ViewLogin',
    Layout: LayoutAuth,
    isPublicOnly: true,
  },
  {
    path: PAGES.USER_CREATE,
    element: <UserCreate />,
    name: 'ViewUserCreate',
    Layout: LayoutMain,
    showBackButton: true,
    isPrivate: true,
    title: 'User creation',
  },
  {
    path: PAGES.USER_UPDATE,
    element: <UserCreate />,
    name: 'ViewUserUpdate',
    Layout: LayoutMain,
    showBackButton: true,
    isPrivate: true,
    title: 'User update',
  },
  {
    path: PAGES.ADMIN_USERS,
    element: <AdminUsers />,
    name: 'ViewAdminUsers',
    Layout: LayoutMain,
    showBackButton: false,
    isPrivate: true,
  },
  {
    path: PAGES.ADMIN_GATEWAYS,
    element: <Gateways />,
    name: 'ViewAdminGateways',
    Layout: LayoutMain,
    showBackButton: false,
    isPrivate: true,
  },
  {
    path: PAGES.SENSOR_DETAILS,
    element: <SensorDetails />,
    name: 'ViewSensorDetails',
    Layout: LayoutMain,
    showBackButton: true,
    title: 'Sensor details',
    isPrivate: true,
  },
  {
    path: PAGES.ZONE_DETAILS,
    element: <ZoneDetails />,
    name: 'ViewZoneDetails',
    Layout: LayoutMain,
    showBackButton: true,
    isPrivate: true,
    title: 'Zone details',
  },
  {
    path: PAGES.BAD_REQUEST,
    element: <ErrorPage />,
    name: 'ViewError400',
    Layout: LayoutMain,
    showBackButton: false,
  },
  {
    path: PAGES.UNAUTHORIZED,
    element: <ErrorPage />,
    name: 'ViewError401',
    Layout: LayoutMain,
    showBackButton: false,
  },
  {
    path: PAGES.NOT_ACCESS,
    element: <ErrorPage />,
    name: 'ViewError403',
    Layout: LayoutMain,
    showBackButton: false,
  },
  {
    path: PAGES.NOT_FOUND,
    element: <ErrorPage />,
    name: 'ViewError404',
    Layout: LayoutMain,
    showBackButton: false,
  },
  {
    path: PAGES.SERVER_ERROR,
    element: <ErrorPage />,
    name: 'ViewError500',
    Layout: LayoutMain,
    showBackButton: false,
  },
  {
    path: PAGES.SUBSCRIPTION,
    element: <SubscriptionView />,
    name: 'ViewSubscription',
    Layout: LayoutMain,
    showBackButton: false,
    isPrivate: true,
  },
  {
    path: PAGES.SUBSCRIPTION_CANCEL,
    element: <Navigate to={PAGES.SUBSCRIPTION} />,
    name: 'ViewSubscriptionCancel',
    Layout: LayoutMain,
    isPrivate: true,
  },
];

export default clientRoutes;
